import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../../store/root-reducer';
import PurchasePhoneNumberTabForm from './purchase-phone-number-form';
import {
  AddPhoneNumbersToCartPayload,
  RemovePhoneNumberFromCartPayload,
  PhoneNumberCartItem,
  PhoneNumberData,
  PurchasePhoneNumbersCartPayload,
} from '../../../../types/phone-numbers';
import { transformPhoneNumbersData } from '../../../../helpers/utils/get-formated-numbers';
import {
  addPhoneNumbersToCartRequest,
  removePhoneNumberFromCartRequest,
  getPhoneNumbersCartRequest,
  purchasePhoneNumbersCartRequest,
} from '../../../../extra-actions';
import {
  resetPhoneNumbersCart,
  resetPurchasePhoneNumbersCartRequest,
} from '../../../../dialer-slice';
import { paddlePollingThunk } from '../../../../../settings/components/billing-subscription/extra-actions';
import {
  getPostLoadMetaRequest,
  getUserSettingsRequest,
} from '../../../../../home/extra-actions';
import { selectPaddleCustomerId } from '../../../../../home/home-slice';
import type {
  PaddleApplicationType,
  PaddlePollingState,
} from '../../../../../settings/components/billing-subscription/types/paddle';
import { RequestStatus } from '../../../../../../shared/enums/request-status';

export interface IProps {}
const PurchasePhoneNumberFormContainer = memo((props: IProps) => {
  const dispatch = useDispatch();

  const phoneNumbersInCart = useSelector(
    (state: RootState) => state.dialer.phoneNumbersInCart,
  );
  const hasErrorsInContactDetailsForm = useSelector(
    (state: RootState) => state.dialer.hasErrorsInContactDetailsForm,
  );
  const phoneNumbersList = useSelector(
    (state: RootState) => state.dialer.phoneNumbersList,
  );
  const removePhoneNumberFromCartRequestState = useSelector(
    (state: RootState) => state.dialer.removePhoneNumberFromCartRequest,
  );
  const purchasePhoneNumbersCartRequestState = useSelector(
    (state: RootState) => state.dialer.purchasePhoneNumbersCartRequest,
  );
  const purchasePhoneNumbersCartResponse = useSelector(
    (state: RootState) => state.dialer.purchasePhoneNumbersCartResponse,
  );
  const shAccountId = useSelector(
    (state: RootState) => state.home?.shAccountId,
  );
  const userEmail = useSelector((state: RootState) => state.home?.email);
  const isDemoAccount = useSelector(
    (state: RootState) => state.home?.isDemoAccount,
  );
  const paddleCustomerId = useSelector(selectPaddleCustomerId);

  const handleAddPhoneNumbersToCartRequest = useCallback(
    (payload: AddPhoneNumbersToCartPayload) => {
      (dispatch(addPhoneNumbersToCartRequest(payload)) as any).then(
        (result) => {
          // Reload cart after successful add to get real DB ids for free number computation
          if (result.meta?.requestStatus === 'fulfilled') {
            dispatch(getPhoneNumbersCartRequest());
          }
        },
      );
    },
    [dispatch],
  );

  const handleRemovePhoneNumberFromCartRequest = useCallback(
    (payload: RemovePhoneNumberFromCartPayload) => {
      dispatch(removePhoneNumberFromCartRequest(payload));
    },
    [dispatch],
  );

  const handleGetPhoneNumbersCartRequest = useCallback(() => {
    dispatch(getPhoneNumbersCartRequest());
  }, [dispatch]);

  const handlePurchasePhoneNumbersCartRequest = useCallback(
    (payload: PurchasePhoneNumbersCartPayload) => {
      dispatch(purchasePhoneNumbersCartRequest(payload));
    },
    [dispatch],
  );

  const handleResetPurchasePhoneNumbersCartRequest = useCallback(() => {
    dispatch(resetPurchasePhoneNumbersCartRequest());
  }, [dispatch]);

  const paddlePolling = useSelector(
    (state: RootState) => state.billingAndSubscription.paddlePolling,
  );

  const accountUsageQuotaRemaining = useSelector(
    (state: RootState) =>
      state.home.getPostLoadMetaResponse?.accountUsageQuotaRemaining,
  );

  useEffect(() => {
    dispatch(getPostLoadMetaRequest());
  }, [dispatch]);

  const freeNumbersQuota = useMemo(() => {
    const quota = accountUsageQuotaRemaining?.['DIALER.PHONE_NUMBERS'];
    return quota ? parseInt(quota, 10) || 0 : 0;
  }, [accountUsageQuotaRemaining]);

  const handleStartPaddlePollingThunk = useCallback(
    (payload: {
      shAccountId: number;
      applicationType: PaddleApplicationType;
      planId?: number;
    }) => dispatch(paddlePollingThunk(payload)),
    [dispatch],
  );

  const formattedPhoneNumbersData: PhoneNumberData[] = useMemo(
    () => transformPhoneNumbersData(phoneNumbersList),
    [phoneNumbersList],
  );

  const clearAllCart = useCallback(() => {
    dispatch(resetPhoneNumbersCart());
  }, [dispatch]);

  const handleRefreshUserSettings = useCallback(() => {
    dispatch(getUserSettingsRequest());
  }, [dispatch]);

  const phoneNumbersInCartWithFreeFlag = useMemo(() => {
    if (freeNumbersQuota <= 0) {
      return phoneNumbersInCart.map((item) => ({
        ...item,
        isFreeNumber: false,
      }));
    }

    // Collect indices of US numbers, sorted by id ascending.
    // Items without a real DB id (id === 0) are pushed to the end
    // so they never steal free slots from items with confirmed ids.
    const usIndices: { index: number; id: number }[] = [];
    phoneNumbersInCart.forEach((item, index) => {
      if (item.countryISOCode === 'US') {
        usIndices.push({ index, id: item.id });
      }
    });
    usIndices.sort((a, b) => {
      // Items without a DB id go last
      if (a.id === 0 && b.id !== 0) return 1;
      if (a.id !== 0 && b.id === 0) return -1;
      return a.id - b.id;
    });

    // First N US numbers (by id asc) are free
    const freeIndices = new Set(
      usIndices.slice(0, freeNumbersQuota).map((entry) => entry.index),
    );

    return phoneNumbersInCart.map((item, index) => ({
      ...item,
      isFreeNumber: freeIndices.has(index),
    }));
  }, [phoneNumbersInCart, freeNumbersQuota]);

  return (
    <PurchasePhoneNumberTabForm
      {...props}
      phoneNumbersInCart={phoneNumbersInCartWithFreeFlag}
      hasErrorsInContactDetailsForm={hasErrorsInContactDetailsForm}
      formattedPhoneNumbersData={formattedPhoneNumbersData}
      addPhoneNumbersToCartRequest={handleAddPhoneNumbersToCartRequest}
      removePhoneNumberFromCartRequest={handleRemovePhoneNumberFromCartRequest}
      getPhoneNumbersCartRequest={handleGetPhoneNumbersCartRequest}
      purchasePhoneNumbersCartRequest={handlePurchasePhoneNumbersCartRequest}
      resetPurchasePhoneNumbersCartRequest={
        handleResetPurchasePhoneNumbersCartRequest
      }
      removePhoneNumberFromCartRequestState={
        removePhoneNumberFromCartRequestState
      }
      purchasePhoneNumbersCartRequestState={
        purchasePhoneNumbersCartRequestState
      }
      purchasePhoneNumbersCartResponse={purchasePhoneNumbersCartResponse}
      shAccountId={shAccountId}
      userEmail={userEmail}
      paddleCustomerId={paddleCustomerId}
      paddlePolling={paddlePolling}
      startPaddlePollingThunk={handleStartPaddlePollingThunk}
      clearAllCart={clearAllCart}
      freeNumbersQuota={freeNumbersQuota}
      refreshUserSettings={handleRefreshUserSettings}
      isDemoAccount={isDemoAccount}
    />
  );
});

export type PurchasePhoneNumberFormProps = IProps & {
  phoneNumbersInCart: PhoneNumberCartItem[];
  hasErrorsInContactDetailsForm: boolean;
  formattedPhoneNumbersData: PhoneNumberData[];
  addPhoneNumbersToCartRequest: (payload: AddPhoneNumbersToCartPayload) => void;
  removePhoneNumberFromCartRequest: (
    payload: RemovePhoneNumberFromCartPayload,
  ) => void;
  getPhoneNumbersCartRequest: () => void;
  purchasePhoneNumbersCartRequest: (
    payload: PurchasePhoneNumbersCartPayload,
  ) => void;
  resetPurchasePhoneNumbersCartRequest: () => void;
  removePhoneNumberFromCartRequestState: {
    [phoneNumber: string]: {
      status: RequestStatus;
      message: string;
      error: any;
    };
  };
  purchasePhoneNumbersCartRequestState: {
    status: RequestStatus;
    message: string;
    error: any;
  };
  purchasePhoneNumbersCartResponse: {
    paddleTransactionId: string;
    dialerCartId?: number;
  } | null;
  shAccountId: number;
  userEmail: string;
  paddleCustomerId: string | undefined;
  paddlePolling: PaddlePollingState;
  startPaddlePollingThunk: (payload: {
    shAccountId: number;
    applicationType: PaddleApplicationType;
    planId?: number;
    dialerCartId?: number;
  }) => void;
  clearAllCart: () => void;
  freeNumbersQuota: number;
  refreshUserSettings: () => void;
  isDemoAccount?: boolean;
};

export default PurchasePhoneNumberFormContainer;
